import axios, { AxiosRequestConfig } from "axios";
import store from "@lhd/app/store";
import { userLogout } from "@lhd/features/users/usersSlice";
import {
  getAccessToken,
  getClient,
  getCompanyId,
  getUid,
  removeCookie,
  setCookie,
} from "@lhd/utility";
import { setAxiosLoading } from "@lhd/hooks";
import { disconnectSharedCable } from "./actionCable";

let abortController = new AbortController();

export const cancelAllRequests = () => {
  abortController.abort();
  abortController = new AbortController();
};

const axiosInstanceV2 = axios.create({
  baseURL: process.env.REACT_APP_API_URL_V2,
});

const persistAuthHeaders = (headers?: Record<string, any>) => {
  const accessToken = headers?.["access-token"];
  const client = headers?.client;
  const uid = headers?.uid;
  const expiry = headers?.expiry;

  const currentExpiry = localStorage.getItem("expiry");
  if (expiry && currentExpiry) {
    if (Number(expiry) < Number(currentExpiry)) {
      return;
    }
  }

  if (accessToken) setCookie("access_token", accessToken);
  if (client) localStorage.setItem("client", client);
  if (uid) localStorage.setItem("uid", uid);
  if (expiry) localStorage.setItem("expiry", expiry);
};

// Add a request interceptor
axiosInstanceV2.interceptors.request.use(
  function (config: AxiosRequestConfig): AxiosRequestConfig {
    // Track loading start with URL - Skip for chat-related URLs to prevent global re-renders
    const url = config.url || "";
    const isChatRequest =
      url.includes("/chat_threads") ||
      url.includes("/threads") ||
      url.includes("/chat_statuses") ||
      url.includes("/chat_categories") ||
      url.includes("/chat_attachments");

    if (!isChatRequest) {
      setAxiosLoading(true, url);
    }

    if (config.headers) {
      config.headers = {
        ...config.headers,
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: "0",
      };

      if (getAccessToken()) {
        config.headers = {
          ...config.headers,
          "access-token": getAccessToken(),
          client: getClient(),
          uid: getUid(),
          "ngrok-skip-browser-warning": "true",
          "X-Login-Company-Id": getCompanyId(),
        };
      }
    }
    return { ...config, signal: abortController.signal };
  },

  function (error) {
    // Track loading end on error with URL
    const url = error?.config?.url || "";
    const isChatRequest =
      url.includes("/chat_threads") ||
      url.includes("/threads") ||
      url.includes("/chat_statuses") ||
      url.includes("/chat_categories") ||
      url.includes("/chat_attachments");

    if (!isChatRequest) {
      setAxiosLoading(false, url);
    }
    return Promise.reject(error);
  },
);

// Add a response interceptor
axiosInstanceV2.interceptors.response.use(
  function (response) {
    // Track loading end on success with URL - Skip for chat-related URLs
    const url = response?.config?.url || "";
    const isChatRequest =
      url.includes("/chat_threads") ||
      url.includes("/threads") ||
      url.includes("/chat_statuses") ||
      url.includes("/chat_categories") ||
      url.includes("/chat_attachments");

    if (!isChatRequest) {
      setAxiosLoading(false, url);
    }
    persistAuthHeaders(response?.headers);
    return response;
  },

  function (error) {
    // Track loading end on error with URL - Skip for chat-related URLs
    const url = error?.config?.url || "";
    const isChatRequest =
      url?.includes("/chat_threads") ||
      url?.includes("/threads") ||
      url?.includes("/chat_statuses") ||
      url?.includes("/chat_categories") ||
      url?.includes("/chat_attachments");

    if (!isChatRequest) {
      setAxiosLoading(false, url);
    }

    if (
      error.response.status === 401 &&
      window.location.pathname !== "/login"
    ) {
      disconnectSharedCable();
      cancelAllRequests();
      store.dispatch(userLogout());
      localStorage.clear();
      removeCookie("access_token");
      setTimeout(() => {
        localStorage.setItem("error_401", "true");
        window.location.replace(`/login`);
      }, 500);
    }
    return Promise.reject(error);
  },
);

export default axiosInstanceV2;
